// Header.js — synced with SixthSens site nav for uniformity
import React, { useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "./Header.css";
import "bootstrap-icons/font/bootstrap-icons.css";

const SITE = "https://sixthsens.ai";

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);

  // Keep body.home-light so shared style.css theme applies
  useEffect(() => {
    document.body.classList.add("home-light");
    return () => {
      document.body.classList.remove("home-light");
    };
  }, []);

  // Close mobile menu on resize back to desktop
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 1200) {
        setMobileOpen(false);
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Toggle nested dropdowns only when mobile menu is open
  const onDropdownToggle = (e) => {
    if (!mobileOpen) return;
    e.preventDefault();
    const submenu = e.currentTarget.nextElementSibling;
    if (submenu) {
      submenu.classList.toggle("dropdown-active");
    }
  };

  const closeMobile = () => setMobileOpen(false);

  return (
    <header id="header" className="fixed-top">
      <div className="container d-flex align-items-center">
        <h1 className="logo me-auto">
          <a href={`${SITE}/index.html`}>SixthSens AI</a>
        </h1>

        <nav
          id="navbar"
          className={`navbar${mobileOpen ? " navbar-mobile" : ""}`}
        >
          <ul>
            <li className="dropdown">
              <a href={`${SITE}/#partnerships`} onClick={onDropdownToggle}>
                <span>Partners</span> <i className="bi bi-chevron-down"></i>
              </a>
              <ul>
                <li>
                  <a href={`${SITE}/partners/google_cloud.html`} onClick={closeMobile}>
                    Google Cloud
                  </a>
                </li>
                <li>
                  <a href={`${SITE}/partners/aws.html`} onClick={closeMobile}>
                    AWS
                  </a>
                </li>
              </ul>
            </li>

            <li className="dropdown">
              <a href={`${SITE}/#products`} onClick={onDropdownToggle}>
                <span>Products</span> <i className="bi bi-chevron-down"></i>
              </a>
              <ul>
                <li>
                  <a
                    href="https://recruitbots.ai"
                    target="_blank"
                    rel="noreferrer"
                    onClick={closeMobile}
                  >
                    RecruitBots
                  </a>
                </li>
                <li>
                  <a
                    href="https://agentforces.ai"
                    target="_blank"
                    rel="noreferrer"
                    onClick={closeMobile}
                  >
                    AgentForces
                  </a>
                </li>
              </ul>
            </li>

            <li className="dropdown">
              <a href={`${SITE}/#services`} onClick={onDropdownToggle}>
                <span>Applications</span>{" "}
                <i className="bi bi-chevron-down"></i>
              </a>
              <ul>
                <li className="dropdown">
                  <a
                    href={`${SITE}/solutions/vision_ai.html`}
                    onClick={onDropdownToggle}
                  >
                    <span>Solutions</span>
                    <i className="bi bi-chevron-down"></i>
                  </a>
                  <ul>
                    <li>
                      <a
                        href={`${SITE}/solutions/vision_ai.html`}
                        onClick={closeMobile}
                      >
                        Vision AI
                      </a>
                    </li>
                    <li>
                      <a
                        href={`${SITE}/solutions/voice_ai.html`}
                        onClick={closeMobile}
                      >
                        Voice AI
                      </a>
                    </li>
                    <li>
                      <a
                        href={`${SITE}/solutions/text_ai.html`}
                        onClick={closeMobile}
                      >
                        Text AI
                      </a>
                    </li>
                    <li>
                      <a
                        href={`${SITE}/solutions/generative_ai.html`}
                        onClick={closeMobile}
                      >
                        Generative AI
                      </a>
                    </li>
                    <li>
                      <a
                        href={`${SITE}/solutions/automation.html`}
                        onClick={closeMobile}
                      >
                        Automation
                      </a>
                    </li>
                    <li>
                      <a
                        href={`${SITE}/solutions/data_intelligence.html`}
                        onClick={closeMobile}
                      >
                        Data Intelligence
                      </a>
                    </li>
                  </ul>
                </li>

                <li className="dropdown">
                  <a href="#casestudies" onClick={onDropdownToggle}>
                    <span>Case Studies</span>{" "}
                    <i className="bi bi-chevron-down"></i>
                  </a>
                  <ul>
                    <li>
                      <a
                        href={`${SITE}/case_studies/automated_gas_refilling.html`}
                        onClick={closeMobile}
                      >
                        Automated Gas Refilling through Machine Vision
                      </a>
                    </li>
                    <li>
                      <a
                        href={`${SITE}/case_studies/streamlined_supply_chain.html`}
                        onClick={closeMobile}
                      >
                        Streamlined Supply Chain Financing Process
                      </a>
                    </li>
                    <li>
                      <a
                        href={`${SITE}/case_studies/automating_lpg_cylinder.html`}
                        onClick={closeMobile}
                      >
                        Automating LPG Cylinder Handling with AI
                      </a>
                    </li>
                    <li>
                      <a
                        href={`${SITE}/case_studies/enhancing_worker_safety.html`}
                        onClick={closeMobile}
                      >
                        Enhancing Worker Safety with Image Analysis
                      </a>
                    </li>
                    <li>
                      <a
                        href={`${SITE}/case_studies/transforming_exercise_experience.html`}
                        onClick={closeMobile}
                      >
                        Transforming Exercise Experience with Mixed Reality
                      </a>
                    </li>
                    <li>
                      <a
                        href={`${SITE}/case_studies/extracting_insights.html`}
                        onClick={closeMobile}
                      >
                        Extracting Insights from Medical Big Data
                      </a>
                    </li>
                    <li>
                      <a
                        href={`${SITE}/case_studies/empowering_leadership_development.html`}
                        onClick={closeMobile}
                      >
                        Empowering Leadership Development with Data Model &amp;
                        BI
                      </a>
                    </li>
                    <li>
                      <a
                        href={`${SITE}/case_studies/revolutionizing_course.html`}
                        onClick={closeMobile}
                      >
                        Revolutionizing Course Discovery with Mobile App
                      </a>
                    </li>
                    <li>
                      <a
                        href={`${SITE}/case_studies/predictive_data_models.html`}
                        onClick={closeMobile}
                      >
                        Predictive Data Models for BPO Call Center
                      </a>
                    </li>
                    <li>
                      <a
                        href={`${SITE}/case_studies/ai_powered_sales.html`}
                        onClick={closeMobile}
                      >
                        AI-powered Sales Platform for Personalized Pitches
                      </a>
                    </li>
                    <li>
                      <a
                        href={`${SITE}/case_studies/streamlining_manufacturing.html`}
                        onClick={closeMobile}
                      >
                        Streamlining Manufacturing Security with Nvidia Tesla
                        V100 GPUs
                      </a>
                    </li>
                  </ul>
                </li>
              </ul>
            </li>

            <li className="dropdown">
              <a href={`${SITE}/#industry4`} onClick={onDropdownToggle}>
                <span>Industries</span> <i className="bi bi-chevron-down"></i>
              </a>
              <ul>
                <li>
                  <a href={`${SITE}/industry/edTech.html`} onClick={closeMobile}>
                    EdTech
                  </a>
                </li>
                <li>
                  <a
                    href={`${SITE}/industry/manufacturing.html`}
                    onClick={closeMobile}
                  >
                    Manufacturing
                  </a>
                </li>
                <li>
                  <a
                    href={`${SITE}/industry/public_sector.html`}
                    onClick={closeMobile}
                  >
                    Public Sector
                  </a>
                </li>
                <li>
                  <a href={`${SITE}/industry/sports.html`} onClick={closeMobile}>
                    Sports
                  </a>
                </li>
                <li>
                  <a href={`${SITE}/industry/rcpg.html`} onClick={closeMobile}>
                    Retail &amp; CPG
                  </a>
                </li>
                <li>
                  <a
                    href={`${SITE}/industry/media_environment.html`}
                    onClick={closeMobile}
                  >
                    Media &amp; Entertainment
                  </a>
                </li>
                <li>
                  <a
                    href={`${SITE}/industry/health_life_sciences_sector.html`}
                    onClick={closeMobile}
                  >
                    Health and Life Sciences
                  </a>
                </li>
                <li>
                  <a href={`${SITE}/industry/bfsi.html`} onClick={closeMobile}>
                    Banking and Financial Sector
                  </a>
                </li>
              </ul>
            </li>

            <li className="dropdown">
              <a href="#company" onClick={onDropdownToggle}>
                <span>Company</span> <i className="bi bi-chevron-down"></i>
              </a>
              <ul>
                <li>
                  <a
                    className="nav-link scrollto"
                    href={`${SITE}/#about`}
                    onClick={closeMobile}
                  >
                    About
                  </a>
                </li>
                <li>
                  <a
                    className="nav-link scrollto"
                    href="#footer"
                    onClick={closeMobile}
                  >
                    Contact
                  </a>
                </li>
              </ul>
            </li>

            <li>
              <a
                className="nav-link scrollto"
                href={`${SITE}/careers.html`}
                onClick={closeMobile}
              >
                Careers
              </a>
            </li>
            <li>
              {/* Already on marketplace — keep CTA pointing home of app */}
              <a className="getstarted scrollto" href="/" onClick={closeMobile}>
                Explore Marketplace
              </a>
            </li>
          </ul>

          {/* Mobile hamburger / close icon */}
          <i
            className={`bi mobile-nav-toggle ${
              mobileOpen ? "bi-x" : "bi-list"
            }`}
            onClick={() => setMobileOpen((open) => !open)}
            role="button"
            aria-label="Toggle navigation"
          ></i>
        </nav>
      </div>
    </header>
  );
};

export default Header;
